import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { ExportSettings, ProjectDoc, RecordedSegment, Take, Track } from '../types.js';
import { useStudioStore, type PanelTab } from '../stores/useStudioStore.js';
import { session } from '../session.js';
import { formatMs, formatTime, clamp, gainToDb, uid } from '../utils/misc.js';
import { mergeSegments, toMonoBuffer } from '../audio/buffers.js';
import { buildPunchedTake, createTrack } from '../audio/projectUtils.js';
import { autoSyncFromClap } from '../audio/SyncEngine.js';
import { reduceNoise } from '../audio/noiseReduction.js';
import { audioBufferToWav } from '../audio/wav.js';
import { Icon, type IconName } from '../components/Icon.js';
import { Waveform } from '../components/Waveform.js';
import { YouTubePlayer } from '../components/YouTubePlayer.js';
import { RecordPanel, MixPanel, FxPanel, SyncPanel, TakesPanel, TimelinePanel, AnalysisPanel, ExportPanel, ProjectTitleEditor } from '../components/StudioPanels.js';
import { projectStorage } from '../audio/ProjectStorageEngine.js';
import { formatBytes } from '../utils/misc.js';
import { ExportEngine, type ExportCapability, type RenderTrackInput } from '../audio/ExportEngine.js';

interface StudioProps {
  onHome: () => void;
  onOpenProjects: () => void;
  onImportInstrumental: (file: File, addToCurrent?: boolean) => Promise<void>;
}

const TAB_META: { id: PanelTab; label: string; mobile: string; icon: IconName }[] = [
  { id: 'record', label: 'GRAVAR', mobile: 'GRAVAR', icon: 'mic' },
  { id: 'timeline', label: 'TIMELINE', mobile: 'TIMELINE', icon: 'wave' },
  { id: 'mix', label: 'MIXER', mobile: 'MIX', icon: 'mix' },
  { id: 'fx', label: 'EFEITOS', mobile: 'FX', icon: 'sparkle' },
  { id: 'sync', label: 'SINCRONIZAÇÃO', mobile: 'SYNC', icon: 'sync' },
  { id: 'takes', label: 'TAKES', mobile: 'TAKES', icon: 'layers' as IconName },
  { id: 'analysis', label: 'ANÁLISE', mobile: 'ANÁLISE', icon: 'wave' },
  { id: 'export', label: 'EXPORTAR', mobile: 'EXPORTAR', icon: 'export' },
];

const COUNTDOWN: Record<ProjectDoc['ui']['countdown'], { beats?: number; seconds?: number }> = {
  none: {}, beats2: { beats: 2 }, beats4: { beats: 4 }, sec3: { seconds: 3 }, sec5: { seconds: 5 },
};

export function Studio({ onHome, onOpenProjects, onImportInstrumental }: StudioProps) {
  const project = useStudioStore((s) => s.project);
  const screen = useStudioStore((s) => s.screen);
  const panel = useStudioStore((s) => s.panel);
  const setPanel = useStudioStore((s) => s.setPanel);
  const updateProject = useStudioStore((s) => s.updateProject);
  const patchTrack = useStudioStore((s) => s.patchTrack);
  const setToast = useStudioStore((s) => s.setToast);
  const setMic = useStudioStore((s) => s.setMic);
  const micReady = useStudioStore((s) => s.micReady);
  const micStatus = useStudioStore((s) => s.micStatus);
  const recording = useStudioStore((s) => s.recording);
  const recordingPaused = useStudioStore((s) => s.recordingPaused);
  const recordingSeconds = useStudioStore((s) => s.recordingSeconds);
  const countdownLabel = useStudioStore((s) => s.countdownLabel);
  const playerState = useStudioStore((s) => s.playerState);
  const playerCurrentTime = useStudioStore((s) => s.playerCurrentTime);
  const playerDuration = useStudioStore((s) => s.playerDuration);
  const playerReady = useStudioStore((s) => s.playerReady);
  const sourceError = useStudioStore((s) => s.sourceError);
  const selectedRegion = useStudioStore((s) => s.selectedRegion);
  const setSelectedRegion = useStudioStore((s) => s.setSelectedRegion);
  const setRecordingState = useStudioStore((s) => s.setRecording);
  const setOperationBusy = useStudioStore((s) => s.setOperationBusy);
  const operationBusy = useStudioStore((s) => s.operationBusy);
  const setAnalysisBusy = useStudioStore((s) => s.setAnalysisBusy);
  const analysisBusy = useStudioStore((s) => s.analysisBusy);
  const setExportBusy = useStudioStore((s) => s.setExportBusy);
  const exportBusy = useStudioStore((s) => s.exportBusy);
  const setSaving = useStudioStore((s) => s.setSaving);
  const dirty = useStudioStore((s) => s.dirty);
  const saving = useStudioStore((s) => s.saving);
  const savedAt = useStudioStore((s) => s.savedAt);
  const isSimpleMode = useStudioStore((s) => s.isSimpleMode);
  const setSimpleMode = useStudioStore((s) => s.setSimpleMode);
  const [exportCapabilities, setExportCapabilities] = useState<ExportCapability | null>(null);
  const [calibrationBusy, setCalibrationBusy] = useState(false);
  const [denoising, setDenoising] = useState(false);
  const [compareTakeId, setCompareTakeId] = useState<string | null>(null);
  const [beforeAfterProcessed, setBeforeAfterProcessed] = useState(true);
  const [importModal, setImportModal] = useState(false);
  const [importRights, setImportRights] = useState(false);
  const [importBusy, setImportBusy] = useState(false);
  const addInstrumentRef = useRef<HTMLInputElement>(null);
  const fileImportRef = useRef<HTMLInputElement>(null);
  const lastProjectId = useRef<string | null>(null);
  const startedAtRef = useRef(0);
  const elapsedRef = useRef(0);
  const punchAutoStopRef = useRef(false);
  const activePunchRef = useRef<{ start: number; end: number } | null>(null);

  if (!project) return null;
  const currentProject = project;
  const leadTrack = currentProject.tracks.find((t) => t.kind === 'lead') ?? currentProject.tracks[0];
  const activeTake = leadTrack?.activeTakeId ? currentProject.takes.find((t) => t.id === leadTrack.activeTakeId) : null;
  const duration = Math.max(
    playerDuration,
    currentProject.instrumental?.duration ?? 0,
    currentProject.instrumentalBuffer?.duration ?? 0,
    ...[...session.trackData.values()].map((d) => d.length / session.sampleRate),
    ...currentProject.takes.flatMap((take) => take.segments.map((seg) => seg.position + seg.samples.length / seg.sampleRate)),
  );
  const youtubeMode = currentProject.sourceType === 'youtube';
  const hasInstrument = !!currentProject.instrumentalBuffer;
  const hasVocal = currentProject.takes.length > 0;
  const currentPosition = playerCurrentTime;

  // Troca de projeto: limpa runtime, não o armazenamento.
  useEffect(() => {
    if (lastProjectId.current === currentProject.id) return;
    session.playback.stop();
    if (lastProjectId.current) {
      const old = useStudioStore.getState().project;
      old?.tracks.forEach((t) => session.audio.removeTrackChain(t.id));
    }
    session.trackData.clear();
    session.trackDenoised.clear();
    session.noiseProfiles.clear();
    session.instrumental = null;
    if (currentProject.instrumentalBuffer) {
      const b = currentProject.instrumentalBuffer;
      session.instrumental = {
        buffer: b,
        left: Float32Array.from(b.getChannelData(0)),
        right: b.numberOfChannels > 1 ? Float32Array.from(b.getChannelData(1)) : Float32Array.from(b.getChannelData(0)),
        sampleRate: b.sampleRate,
        duration: b.duration,
        name: currentProject.instrumental?.name ?? 'Instrumental autorizado',
      };
    }
    // O adapter do player é gerenciado pelo efeito do YouTubePlayer (monta após
    // o pai no React); não o zerar aqui para não apagar um adapter recém-criado.
    session.playback.setInstrumentalBuffer(null);
    lastProjectId.current = currentProject.id;
    // restaura o estado de sync salvo no documento
    session.sync.manualOffsetMs = currentProject.sync.latencyOffsetMs;
    session.sync.inputLatencyMs = currentProject.sync.inputLatencyMs ?? 0;
    session.sync.outputLatencyMs = currentProject.sync.outputLatencyMs;
    session.sync.playerStartDelayMs = currentProject.sync.playerStartDelayMs;
    session.sync.driftCorrectionMs = currentProject.sync.driftCorrectionMs;
    setMic({ micReady: false, micDevices: [], micLevel: { peak: 0, rms: 0 }, micStatus: 'idle', micError: null });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentProject.id]);

  // Restaura o reforço de gravação do projeto (sinal original por padrão).
  useEffect(() => {
    session.recorder.setRecordingGainBoostPercent(currentProject.ui.recordingGainBoostPercent ?? 0);
  }, [currentProject.id, currentProject.ui.recordingGainBoostPercent]);

  // Setup do player não depende do AudioContext (o iframe é oficial e separado).
  useEffect(() => {
    let alive = true;
    session.exporter.capabilities().then((caps) => { if (alive) setExportCapabilities(caps); });
    return () => { alive = false; };
  }, []);

  // Medidor e estado do gravador: eventos reais do motor de captura.
  useEffect(() => {
    session.recorder.on({
      onLevel: (level) => useStudioStore.getState().setMic({ micLevel: level }),
      onStateChange: (state) => {
        const mapped = state === 'requesting' ? 'permission' : state === 'error' ? 'error' : state === 'unsupported' ? 'unsupported' : state;
        useStudioStore.getState().setMic({ micStatus: mapped as any });
      },
      onError: (message) => useStudioStore.getState().setMic({ micError: message, micStatus: 'error' }),
      onNoInput: () => useStudioStore.getState().setMic({ micLevel: { peak: 0, rms: 0 } }),
    });
  }, []);

  // Autosave local com debounce.
  useEffect(() => {
    const doc = currentProject;
    const bytes = doc.takes.reduce((sum, t) => sum + t.segments.reduce((a, seg) => a + seg.samples.byteLength, 0), 0) + (doc.instrumentalBuffer ? doc.instrumentalBuffer.length * doc.instrumentalBuffer.numberOfChannels * 4 : 0);
    const summary = {
      id: doc.id,
      title: doc.title,
      createdAt: doc.createdAt,
      updatedAt: Date.now(),
      sourceType: doc.sourceType,
      youtubeVideoId: doc.youtubeVideoId,
      youtubeTitle: doc.youtubeTitle,
      instrumentalName: doc.instrumental?.name,
      durationSec: Math.max(playerDuration, doc.instrumental?.duration ?? 0, ...doc.takes.flatMap((t) => t.segments.map((s) => s.position + s.samples.length / s.sampleRate))),
      takes: doc.takes.length,
      tracks: doc.tracks.length,
      bytes,
    };
    setSaving(true);
    projectStorage.scheduleSave(doc, summary, 1400, (err) => {
      setSaving(false, err ? null : Date.now());
      if (err) {
        useStudioStore.getState().setDirty(true);
        setToast({ tone: 'warning', title: 'O autosave não concluiu', description: err });
      }
    });
    return () => projectStorage.cancelScheduled();
  }, [currentProject, playerDuration, setSaving, setToast]);

  // Poll do transporte oficial / local e timer do take. 10 Hz, sem polling pesado.
  useEffect(() => {
    const id = window.setInterval(() => {
      const time = session.playback.status === 'playing'
        ? session.playback.getMediaTime()
        : session.adapter?.getCurrentTime() ?? session.playback.getMediaTime();
      const duration = Math.max(session.adapter?.getDuration() ?? 0, session.instrumental?.duration ?? 0, useStudioStore.getState().playerDuration);
      useStudioStore.getState().setPlayer({ playerCurrentTime: time, playerDuration: duration });
      if (recording && !recordingPaused && startedAtRef.current) {
        useStudioStore.getState().setRecording({ recordingSeconds: elapsedRef.current + (performance.now() - startedAtRef.current) / 1000 });
      }
    }, 100);
    return () => clearInterval(id);
  }, [recording, recordingPaused]);

  // Seção punch-in/out: para automaticamente no fim do trecho selecionado.
  useEffect(() => {
    if (!recording || recordingPaused || !selectedRegion || punchAutoStopRef.current) return;
    const id = window.setInterval(() => {
      const time = session.playback.getMediaTime();
      if (time >= selectedRegion.end) {
        punchAutoStopRef.current = true;
        void stopRecording(true);
      }
    }, 60);
    return () => clearInterval(id);
  }, [recording, recordingPaused, selectedRegion]);

  // Monitor vocal (sempre desligado por padrão) segue o toggle de UI.
  useEffect(() => {
    if (!session.audio.ready || !session.recorder.stream) return;
    session.audio.connectMonitor(session.recorder.stream, currentProject.ui.monitor, currentProject.mix.monitorVolume);
  }, [currentProject.ui.monitor, currentProject.mix.monitorVolume, micReady]);

  // Sincroniza configurações do mixer em tempo real (se a cadeia já foi montada).
  useEffect(() => {
    if (!session.audio.ready) return;
    applyMixer(currentProject);
    session.sync.manualOffsetMs = currentProject.sync.latencyOffsetMs;
    session.sync.inputLatencyMs = currentProject.sync.inputLatencyMs ?? 0;
    session.sync.outputLatencyMs = currentProject.sync.outputLatencyMs;
    session.sync.playerStartDelayMs = currentProject.sync.playerStartDelayMs;
    session.sync.driftCorrectionMs = currentProject.sync.driftCorrectionMs;
  }, [currentProject.tracks, currentProject.mix, currentProject.sync]);

  // Atalho M para adicionar marcador no tempo atual.
  useEffect(() => {
    const keydown = (e: KeyboardEvent) => {
      if (e.key.toLowerCase() === 'm' && !(e.target instanceof HTMLInputElement) && !(e.target instanceof HTMLTextAreaElement)) {
        useStudioStore.getState().addMarker(useStudioStore.getState().playerCurrentTime, 'MARCADOR', 'custom');
      }
      if (e.code === 'Space' && !(e.target instanceof HTMLInputElement) && !(e.target instanceof HTMLTextAreaElement)) {
        e.preventDefault();
        if (useStudioStore.getState().recording) void toggleRecordPause();
        else void togglePlayback();
      }
    };
    window.addEventListener('keydown', keydown);
    return () => window.removeEventListener('keydown', keydown);
  });

  const ensureAudio = useCallback(async () => {
    const ctx = await session.audio.ensure();
    const doc = useStudioStore.getState().project;
    if (!doc) return ctx;
    for (const track of doc.tracks) {
      if (!session.audio.getTrack(track.id)) session.audio.createTrackChain(track.id, track.fx);
      let data = session.trackData.get(track.id);
      if (!data) {
        const relevant = doc.takes.filter((t) => t.trackId === track.id);
        const timelineDuration = Math.max(
          doc.instrumental?.duration ?? 0,
          doc.instrumentalBuffer?.duration ?? 0,
          useStudioStore.getState().playerDuration,
          ...relevant.flatMap((take) => take.segments.map((seg) => seg.position + seg.samples.length / seg.sampleRate)),
        );
        if (track.comping && track.regionMap?.length) {
          const built = session.takes.buildTrackAudio(track, doc.takes, { sampleRate: ctx.sampleRate, start: 0, duration: timelineDuration || undefined });
          if (built.data.length) data = built.data;
        }
        if (!data) {
          const take = doc.takes.find((t) => t.id === track.activeTakeId) ??
            [...relevant].sort((a,b)=>b.index-a.index)[0];
          if (take?.segments.length) {
            const merged = mergeSegments(take.segments, { sampleRate: ctx.sampleRate, start: 0, duration: timelineDuration || undefined });
            data = merged.data;
          }
        }
        if (data) {
          session.trackData.set(track.id, data);
          session.audio.setTrackBuffer(track.id, data, ctx.sampleRate);
        }
      } else {
        session.audio.setTrackBuffer(track.id, data, ctx.sampleRate);
      }
      if (track.fx.noiseReduction.enabled && data && !session.trackDenoised.has(track.id)) {
        const reduced = reduceNoise(data, ctx.sampleRate, { amount: track.fx.noiseReduction.amount, floorDb: track.fx.noiseReduction.floorDb });
        session.trackDenoised.set(track.id, reduced.data);
        session.audio.setTrackDenoised(track.id, reduced.data, ctx.sampleRate);
      }
    }
    if (doc.instrumentalBuffer) {
      session.audio.setInstrumentalBuffer(doc.instrumentalBuffer);
      session.playback.setInstrumentalBuffer(doc.instrumentalBuffer);
      const left = Float32Array.from(doc.instrumentalBuffer.getChannelData(0));
      const right = doc.instrumentalBuffer.numberOfChannels > 1
        ? Float32Array.from(doc.instrumentalBuffer.getChannelData(1))
        : Float32Array.from(left);
      session.instrumental = {
        buffer: doc.instrumentalBuffer,
        left,
        right,
        sampleRate: doc.instrumentalBuffer.sampleRate,
        duration: doc.instrumentalBuffer.duration,
        name: doc.instrumental?.name ?? 'Instrumental',
      };
    }
    applyMixer(doc);
    session.sync.manualOffsetMs = doc.sync.latencyOffsetMs;
    return ctx;
  }, []);

  const applyMixer = (doc: ProjectDoc) => {
    if (!session.audio.ready) return;
    for (const track of doc.tracks) {
      if (!session.audio.getTrack(track.id)) session.audio.createTrackChain(track.id, track.fx);
    }
    session.mixer.applyTracks({ tracks: doc.tracks, mix: doc.mix, sampleRate: session.audio.sampleRate });
  };

  const enableMic = async () => {
    setMic({ micError: null, micStatus: 'permission' });
    try {
      const ctx = await ensureAudio();
      await session.recorder.startMetering(ctx);
      const devices = await session.recorder.listDevices();
      const stream = session.recorder.stream;
      if (stream) session.audio.connectMonitor(stream, currentProject.ui.monitor, currentProject.mix.monitorVolume);
      setMic({ micReady: true, micDevices: devices, selectedMicId: session.recorder.stream?.getAudioTracks()[0]?.getSettings().deviceId ?? '', micStatus: 'ready', micError: null });
      const latency = session.audio.measureLatency();
      const trackSettings = session.recorder.stream?.getAudioTracks()[0]?.getSettings() as (MediaTrackSettings & { latency?: number }) | undefined;
      const inputLatencyMs = typeof trackSettings?.latency === 'number' ? trackSettings.latency * 1000 : 0;
      updateProject({ sync: { ...useStudioStore.getState().project!.sync, inputLatencyMs, outputLatencyMs: latency.outputLatencyMs } });
      session.sync.inputLatencyMs = inputLatencyMs;
      session.sync.outputLatencyMs = latency.outputLatencyMs;
      setToast({ tone: 'success', title: 'Microfone pronto', description: session.recorder.fallbackReason || 'Fale ou cante para conferir o nível de entrada.' });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Não foi possível acessar o microfone.';
      setMic({ micReady: false, micStatus: 'error', micError: message });
    }
  };

  const switchMic = async (deviceId: string) => {
    setMic({ micStatus: 'permission', micError: null });
    try {
      await session.recorder.switchDevice(deviceId);
      const devices = await session.recorder.listDevices();
      setMic({ micReady: true, micDevices: devices, selectedMicId: deviceId, micStatus: 'ready' });
      if (session.recorder.stream) session.audio.connectMonitor(session.recorder.stream, currentProject.ui.monitor, currentProject.mix.monitorVolume);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Não foi possível trocar o microfone.';
      setMic({ micReady: false, micStatus: 'error', micError: message });
    }
  };

  const playOptions = (from: number, includeVocal = true) => {
    const doc = useStudioStore.getState().project!;
    const vocals = includeVocal ? doc.tracks.filter((t) => session.trackData.has(t.id) && !t.muted).map((t) => ({
      trackId: t.id,
      fromPosition: Math.max(0, from + (doc.sync.latencyOffsetMs + t.offsetMs) / 1000),
      useDenoised: t.fx.noiseReduction.enabled && session.trackDenoised.has(t.id),
      playbackRate: doc.ui.speed,
    })) : [];
    return {
      from,
      vocals,
      instrumental: doc.sourceType === 'file',
      metronome: doc.ui.metronome ? { bpm: doc.ui.bpm, gain: doc.ui.metronomeVolume } : undefined,
    };
  };

  const persistSyncEvents = () => {
    const events = session.sync.events.slice(-500);
    useStudioStore.getState().updateProject({ syncEvents: events });
  };

  const togglePlayback = async () => {
    const state = useStudioStore.getState();
    const doc = state.project;
    if (!doc) return;
    if (state.recording) { await toggleRecordPause(); return; }
    if (session.playback.status === 'playing') {
      session.playback.pause();
      persistSyncEvents();
      return;
    }
    if (session.playback.status === 'paused') {
      await session.playback.resume();
      persistSyncEvents();
      return;
    }
    try {
      await ensureAudio();
      if (doc.sourceType === 'youtube' && !session.adapter) {
        setToast({ tone: 'warning', title: 'Player do YouTube indisponível', description: 'Aguarde o player carregar ou confira a conexão.' });
        return;
      }
      const from = state.playerCurrentTime >= (state.playerDuration || 0) - 0.25 ? 0 : state.playerCurrentTime;
      const opts = playOptions(from);
      await session.playback.play(opts);
      persistSyncEvents();
    } catch (err) {
      setToast({ tone: 'error', title: 'Não foi possível iniciar', description: err instanceof Error ? err.message : 'Tente novamente.' });
    }
  };

  const stopPlayback = () => {
    if (recording) { void stopRecording(false); return; }
    session.playback.stop();
    persistSyncEvents();
    useStudioStore.getState().setPlayer({ playerCurrentTime: 0 });
  };

  const countdownConfig = (doc: ProjectDoc) => ({ ...COUNTDOWN[doc.ui.countdown], bpm: doc.ui.bpm });

  const startRecording = async (isPunch = false) => {
    const doc = useStudioStore.getState().project;
    if (!doc) return;
    if (!micReady) {
      setToast({ tone: 'info', title: 'Ative o microfone primeiro', description: 'Conecte e teste o nível de entrada antes de gravar.' });
      await enableMic();
      return;
    }
    if (recording) return;
    setOperationBusy(isPunch ? 'punch' : 'record');
    punchAutoStopRef.current = false;
    try {
      const ctx = await ensureAudio();
      const region = isPunch ? useStudioStore.getState().selectedRegion : null;
      activePunchRef.current = region;
      const isAlreadyPlaying = session.playback.status === 'playing';
      if (doc.sourceType === 'youtube' && !session.adapter) throw new Error('O player do YouTube ainda não está pronto.');

      if (isPunch && region) {
        const count = countdownConfig(doc);
        const countLead = count.seconds ? count.seconds : count.beats ? count.beats * 60 / Math.max(30,doc.ui.bpm) : 0;
        const preRoll = 2.2 + countLead;
        const from = Math.max(0, region.start - preRoll);
        await session.playback.play({
          ...playOptions(from, false), from, countdown: count,
          onCountdownStep: ({label})=>setRecordingState({countdownLabel:label}),
        });
        // Pré-roll: a base toca antes do ponto de entrada; capturamos no início
        // da região para não substituir nem deslocar o resto do take.
        const limit = performance.now() + 10000;
        while (session.playback.getMediaTime() < region.start - 0.005 && performance.now() < limit) {
          await new Promise((r) => setTimeout(r, 16));
        }
        if (performance.now() >= limit) throw new Error('Não conseguimos chegar ao trecho selecionado.');
      } else if (!isAlreadyPlaying) {
        const from = currentPosition >= (playerDuration || duration) - 0.3 ? 0 : currentPosition;
        await session.playback.play({
          ...playOptions(from, false), from, countdown: countdownConfig(doc),
          onCountdownStep: ({label})=>setRecordingState({countdownLabel:label}),
        });
      } else if (session.playback.status === 'paused') {
        await session.playback.resume();
      }

      const position = session.playback.getMediaTime();
      const recorderState = useStudioStore.getState();
      const armedTrack = doc.tracks.find((t) => t.armed) ?? doc.tracks.find((t) => t.kind === 'lead') ?? doc.tracks[0];
      const startPerf = performance.now();
      await session.recorder.start({
        position,
        startCtxTime: ctx.currentTime,
        mediaRequestedAtPerf: session.adapter?.getPlayRequestedAtPerf() ?? startPerf,
        deviceId: recorderState.selectedMicId || undefined,
      });
      session.sync.record({ type: 'record-start', ctxTime: ctx.currentTime, mediaTime: position });
      elapsedRef.current = 0;
      startedAtRef.current = performance.now();
      setRecordingState({ recording: true, recordingPaused: false, recordingSeconds: 0, countdownLabel: null });
      setMic({ micStatus: 'recording' });
      if (region && isPunch) {
        setToast({ tone: 'info', title: 'Punch-in ativo', description: `Gravando ${formatTime(region.start)} → ${formatTime(region.end)}. A gravação para sozinha no fim do trecho.` });
      }
      void armedTrack;
    } catch (err) {
      setRecordingState({ recording: false, recordingPaused: false, countdownLabel: null });
      const message = err instanceof Error ? err.message : 'Não foi possível iniciar a gravação.';
      setToast({ tone: 'error', title: 'Gravação não iniciada', description: message });
    } finally {
      setOperationBusy(null);
    }
  };

  const stopRecording = async (auto = false) => {
    if (!useStudioStore.getState().recording) return;
    setOperationBusy('stopping');
    elapsedRef.current += startedAtRef.current ? (performance.now() - startedAtRef.current) / 1000 : 0;
    startedAtRef.current = 0;
    try {
      const result = await session.recorder.stop();
      session.sync.record({ type: 'record-stop', ctxTime: session.audio.currentTime, mediaTime: session.playback.getMediaTime() });
      session.playback.stop();
      const liveDoc = useStudioStore.getState().project;
      if (session.recorder.stream && session.audio.ctx) {
        await session.recorder.startMetering(session.audio.ctx);
        if (liveDoc) session.audio.connectMonitor(session.recorder.stream, liveDoc.ui.monitor, liveDoc.mix.monitorVolume);
      }
      setRecordingState({ recording: false, recordingPaused: false, recordingSeconds: result.durationSec, countdownLabel: null });
      setMic({ micStatus: 'ready', micLevel: { peak: 0, rms: 0 } });
      if (!result.segments.length || result.durationSec < 0.12) {
        setToast({ tone: 'warning', title: 'Não recebemos áudio', description: 'Confira o microfone e grave mais alguns segundos.' });
        return;
      }
      const doc = useStudioStore.getState().project;
      if (!doc) return;
      const targetTrack = doc.tracks.find((t) => t.armed) ?? doc.tracks.find((t) => t.kind === 'lead') ?? doc.tracks[0];
      if (!targetTrack) return;
      const existing = doc.takes.filter((t) => t.trackId === targetTrack.id);
      const index = Math.max(0, ...existing.map((t) => t.index)) + 1;
      const rawTake = session.takes.createTake({
        trackId: targetTrack.id,
        index,
        segments: result.segments,
        mediaStart: result.segments[0]?.position ?? 0,
        diag: {
          micLatencyMs: result.diag.micLatencyMs,
          deviceLabel: result.diag.deviceLabel,
          echoCancellation: result.diag.echoCancellation,
          sampleRate: result.diag.sampleRate,
          pauseCtxTimes: result.pauseEvents,
          resumeCtxTimes: result.resumeEvents,
        },
      });
      let takes = [...doc.takes, rawTake];
      let chosenTake = rawTake;
      const region = activePunchRef.current;
      if (region) {
        const base = existing.find((t) => t.id === targetTrack.activeTakeId) ?? existing[existing.length - 1];
        if (base) {
          const composite = buildPunchedTake(base, rawTake, region, session.audio.sampleRate, index + 1);
          composite.name = `Take ${index + 1} · punch ${formatTime(region.start)}–${formatTime(region.end)}`;
          takes = [...takes, composite];
          chosenTake = composite;
        }
      }
      // A gravação recém-terminada fica sempre selecionada para audição.
      const updatedTracks = doc.tracks.map((t) => t.id === targetTrack.id ? { ...t, activeTakeId: chosenTake.id, armed: true } : t);
      const nextDoc: ProjectDoc = { ...doc, takes, tracks: updatedTracks, syncEvents: session.sync.events.slice(-500), updatedAt: Date.now() };
      const merged = mergeSegments(chosenTake.segments, {
        sampleRate: session.audio.sampleRate,
        start: 0,
        duration: Math.max(session.instrumental?.duration ?? 0, playerDuration, ...chosenTake.segments.map((s) => s.position + s.samples.length / s.sampleRate)) || undefined,
      });
      session.trackData.set(targetTrack.id, merged.data);
      session.trackDenoised.delete(targetTrack.id);
      session.audio.setTrackBuffer(targetTrack.id, merged.data, session.audio.sampleRate);
      session.audio.setTrackDenoised(targetTrack.id, null);
      useStudioStore.getState().updateProject(nextDoc);
      useStudioStore.getState().setPanel('mix');
      setSelectedRegion(null);
      const diagText = result.method === 'mediarecorder' ? 'Fallback MediaRecorder: sincronização pode exigir ajuste manual.' : result.clipped ? 'Atenção: há picos muito altos neste take.' : 'Seu áudio foi processado localmente.';
      setToast({ tone: result.clipped ? 'warning' : 'success', title: chosenTake.name, description: diagText });
    } catch (err) {
      setToast({ tone: 'error', title: 'Erro ao finalizar o take', description: err instanceof Error ? err.message : 'Tente novamente.' });
    } finally {
      setOperationBusy(null);
      punchAutoStopRef.current = false;
      activePunchRef.current = null;
    }
  };

  const toggleRecordPause = async () => {
    if (!useStudioStore.getState().recording) return;
    if (!recordingPaused) {
      elapsedRef.current += startedAtRef.current ? (performance.now() - startedAtRef.current) / 1000 : 0;
      startedAtRef.current = 0;
      session.recorder.pause();
      session.playback.pause();
      persistSyncEvents();
      setRecordingState({ recordingPaused: true, recordingSeconds: elapsedRef.current });
      setMic({ micStatus: 'paused' });
    } else {
      try {
        await session.playback.resume();
        session.recorder.resume({ position: session.playback.getMediaTime() });
        persistSyncEvents();
        startedAtRef.current = performance.now();
        setRecordingState({ recordingPaused: false });
        setMic({ micStatus: 'recording' });
      } catch (err) {
        setToast({ tone: 'error', title: 'Não foi possível continuar', description: err instanceof Error ? err.message : 'Tente novamente.' });
      }
    }
  };

  const createTrackData = async (takeId: string, trackId: string) => {
    const doc = useStudioStore.getState().project;
    if (!doc) return null;
    const take = doc.takes.find((t) => t.id === takeId);
    if (!take) return null;
    const ctx = await ensureAudio();
    const merged = mergeSegments(take.segments, { sampleRate: ctx.sampleRate, start: 0, duration: Math.max(duration, ...take.segments.map((s) => s.position + s.samples.length / s.sampleRate)) || undefined });
    session.trackData.set(trackId, merged.data);
    session.audio.setTrackBuffer(trackId, merged.data, ctx.sampleRate);
    return merged.data;
  };

  const playTake = async (takeId: string) => {
    const doc = useStudioStore.getState().project;
    if (!doc) return;
    const take = doc.takes.find((t) => t.id === takeId);
    if (!take) return;
    const track = doc.tracks.find((t) => t.id === take.trackId);
    if (!track) return;
    patchTrack(track.id, { activeTakeId: takeId, comping: false });
    useStudioStore.getState().setSelectedTakeId(takeId);
    try {
      await createTrackData(takeId, track.id);
      const from = useStudioStore.getState().selectedRegion?.start ?? 0;
      const offset = doc.sync.latencyOffsetMs + track.offsetMs;
      await session.playback.play({
        from,
        vocals: [{ trackId: track.id, fromPosition: Math.max(0, from + offset / 1000), useDenoised: track.fx.noiseReduction.enabled && session.trackDenoised.has(track.id) }],
        instrumental: doc.sourceType === 'file',
      });
      setPanel('record');
    } catch (err) {
      setToast({ tone: 'error', title: 'Não foi possível reproduzir o take', description: err instanceof Error ? err.message : 'Tente novamente.' });
    }
  };

  const deleteTake = (takeId: string) => {
    const doc = useStudioStore.getState().project;
    const take = doc?.takes.find((t) => t.id === takeId);
    if (!doc || !take) return;
    if (!window.confirm(`Apagar ${take.name}? Essa ação não pode ser desfeita.`)) return;
    const takes = doc.takes.filter((t) => t.id !== takeId);
    const track = doc.tracks.find((t) => t.id === take.trackId);
    if (track?.activeTakeId === takeId) {
      const replacement = takes.filter((t) => t.trackId === track.id).sort((a,b)=>b.index-a.index)[0];
      patchTrack(track.id, { activeTakeId: replacement?.id });
      session.trackData.delete(track.id);
    }
    useStudioStore.getState().updateProject({ takes });
    setToast({ tone: 'success', title: 'Take apagado', description: 'Os outros takes do projeto foram preservados.' });
  };

  const toggleCompare = async (takeId: string) => {
    if (!takeId) { setCompareTakeId(null); return; }
    setCompareTakeId(takeId);
    await playTake(takeId);
  };

  const makeComping = () => {
    const doc = useStudioStore.getState().project;
    if (!doc || doc.takes.length < 2) return;
    const track = doc.tracks.find((t) => t.kind === 'lead') ?? doc.tracks[0];
    const takes = doc.takes.filter((t) => t.trackId === track.id).sort((a,b)=>a.index-b.index);
    const d = Math.max(duration, ...takes.flatMap((t)=>t.segments.map((s)=>s.position+s.samples.length/s.sampleRate)));
    if (d <= 0) return;
    const markers = doc.markers.map((m)=>m.time).filter((t)=>t>0&&t<d).sort((a,b)=>a-b);
    const bounds = [0,...markers,d];
    const regions = bounds.slice(0,-1).map((start,i)=>({id:uid('region'),start,end:bounds[i+1],takeId:takes[i%takes.length].id,label:`Parte ${i+1}`})).filter((r)=>r.end-r.start>.2);
    const newTrack = { ...track, comping: true, regionMap: regions, activeTakeId: takes[0]?.id };
    useStudioStore.getState().updateProject({ tracks: doc.tracks.map((t)=>t.id===track.id?newTrack:t) });
    session.trackData.delete(track.id);
    setToast({ tone: 'success', title: 'Comping criado', description: `${regions.length} regiões divididas entre ${takes.length} takes. Ajuste os trechos na aba Takes.` });
  };

  const changePlaybackTime = (time: number) => {
    session.playback.seek(time);
    persistSyncEvents();
    useStudioStore.getState().setPlayer({ playerCurrentTime: time });
  };

  const calibrate = async () => {
    if (!micReady || calibrationBusy) return;
    setCalibrationBusy(true);
    try {
      const ctx = await ensureAudio();
      session.playback.stop();
      const startAt = ctx.currentTime + 0.04;
      const clickAt = startAt + 0.25;
      await session.recorder.start({ position: 0, startCtxTime: startAt });
      session.audio.scheduleClick(clickAt, { freq: 1400, gain: 0.65, duration: 0.07 });
      await new Promise((r) => setTimeout(r, 900));
      const result = await session.recorder.stop();
      const segment = result.segments[0];
      if (!segment?.samples.length) throw new Error('Nenhum sinal captado. Confira o volume do microfone e tente novamente.');
      const delta = autoSyncFromClap({
        vocalData: segment.samples,
        sampleRate: segment.sampleRate,
        vocalStartPosition: 0,
        clickAtMediaTime: clickAt - startAt,
        thresholdDb: -38,
      });
      if (delta.confidence < 0.35) {
        setToast({ tone: 'warning', title: 'Calibração inconclusiva', description: delta.detail });
      } else {
        const doc = useStudioStore.getState().project!;
        const nextOffset = clamp(delta.offsetMs, -500, 500);
        updateProject({ sync: { ...doc.sync, latencyOffsetMs: nextOffset } });
        session.sync.setManual(nextOffset);
        setToast({ tone: 'success', title: `Compensação ${formatMs(nextOffset)}`, description: `${delta.detail} Você pode refinar o valor manualmente.` });
      }
    } catch (err) {
      setToast({ tone: 'error', title: 'Calibração não concluída', description: err instanceof Error ? err.message : 'Tente novamente.' });
    } finally {
      if (session.recorder.stream && session.audio.ctx) {
        await session.recorder.startMetering(session.audio.ctx).catch(() => {});
      }
      setCalibrationBusy(false);
      setMic({ micStatus: 'ready' });
    }
  };

  const runNoiseReduction = async (trackId: string) => {
    const doc = useStudioStore.getState().project;
    const track = doc?.tracks.find((t)=>t.id===trackId);
    if (!doc || !track) return;
    await ensureAudio();
    const data = session.trackData.get(trackId);
    if (!data || !track) {
      setToast({ tone: 'warning', title: 'Grave um take primeiro', description: 'A redução de ruído analisa a faixa vocal selecionada.' });
      return;
    }
    setDenoising(true);
    setOperationBusy('denoise');
    try {
      await new Promise((r)=>setTimeout(r,20));
      const result = reduceNoise(data, session.audio.sampleRate, { amount: track.fx.noiseReduction.amount, floorDb: track.fx.noiseReduction.floorDb });
      session.trackDenoised.set(trackId,result.data);
      session.audio.setTrackDenoised(trackId,result.data,session.audio.sampleRate);
      setToast({ tone: 'success', title: 'Redução de ruído aplicada', description: 'Versão limpa pronta para reprodução e exportação. O take original continua intacto.' });
    } catch (err) {
      setToast({ tone: 'error', title: 'Não foi possível reduzir o ruído', description: err instanceof Error ? err.message : 'Tente novamente.' });
    } finally {
      setDenoising(false); setOperationBusy(null);
    }
  };

  const toggleBeforeAfter = (processed: boolean) => {
    setBeforeAfterProcessed(processed);
    const doc = useStudioStore.getState().project;
    if (!doc || !session.audio.ready) return;
    const track = doc.tracks.find((t)=>t.kind==='lead')??doc.tracks[0];
    if (!track || !session.audio.getTrack(track.id)) return;
    if (processed) session.audio.applyFx(track.id,track.fx);
    else {
      const dry = { ...track.fx, preset: 'dry' as const, highPassOn:false, comp:{...track.fx.comp,enabled:false},reverb:{...track.fx.reverb,enabled:false},delay:{...track.fx.delay,enabled:false},gate:{...track.fx.gate,enabled:false},deEsser:{...track.fx.deEsser,enabled:false},presenceDb:0,lowGainDb:0,midGainDb:0,highGainDb:0,noiseReduction:{...track.fx.noiseReduction,enabled:false} };
      session.audio.applyFx(track.id,dry);
    }
  };

  const analyzeTake = async () => {
    const doc = useStudioStore.getState().project;
    const track = doc?.tracks.find((t)=>t.kind==='lead')??doc?.tracks[0];
    if (!doc || !track) return;
    setAnalysisBusy(true);
    try {
      await ensureAudio();
      const data = session.trackData.get(track.id);
      if (!data?.length) throw new Error('Grave uma voz para analisar.');
      await new Promise((r)=>setTimeout(r,10));
      const analysis = session.analysis.analyze({
        vocal:data,
        sampleRate:session.audio.sampleRate,
        instrumental:session.instrumental?.left ?? null,
        instrumentalSampleRate:session.instrumental?.sampleRate,
        syncOffsetMs:doc.sync.latencyOffsetMs,
        hasReferenceAudio:!!session.instrumental,
        trackName:track.name,
      });
      updateProject({analysis});
      setToast({ tone:'success',title:'Análise pronta',description:'Métricas técnicas e comentários adicionados ao painel.' });
    } catch(err) {
      setToast({tone:'error',title:'Análise não concluída',description:err instanceof Error?err.message:'Tente novamente.'});
    } finally {setAnalysisBusy(false);}
  };

  const exportAudio = async (settings: ExportSettings, kind: 'vocal'|'mix', onlyTrackId?: string) => {
    const doc = useStudioStore.getState().project;
    if (!doc) return;
    setExportBusy(true,8);
    setOperationBusy('export');
    try {
      const ctx = await ensureAudio();
      const tracks: RenderTrackInput[] = [];
      for (const t of doc.tracks.filter((x)=>!onlyTrackId||x.id===onlyTrackId)) {
        const take=doc.takes.find((x)=>x.id===t.activeTakeId)??[...doc.takes].filter((x)=>x.trackId===t.id).sort((a,b)=>b.index-a.index)[0];
        let data=session.trackData.get(t.id);
        if (!data&&take) {
          const merged=mergeSegments(take.segments,{sampleRate:ctx.sampleRate,start:0,duration:Math.max(duration,...take.segments.map((s)=>s.position+s.samples.length/s.sampleRate))||undefined});
          data=merged.data;session.trackData.set(t.id,data);
        }
        if (data) tracks.push({
          id:t.id,name:t.name,data,sampleRate:ctx.sampleRate,offsetSec:-(t.offsetMs+doc.sync.latencyOffsetMs)/1000,
          volume:t.volume,pan:t.pan,fx:t.fx,muted:t.muted,
          useDenoised:t.fx.noiseReduction.enabled,
          denoised:session.trackDenoised.get(t.id)??null,
        });
      }
      const rate=ExportEngine.settingsToRate(settings);
      const instAllowed=kind==='mix'&&!!doc.instrumentalBuffer;
      if(kind==='mix'&&!instAllowed) throw new Error('Para exportar a mix completa, envie um instrumental autorizado.');
      const inst=session.instrumental;
      const renderDuration=Math.max(
        duration,
        ...tracks.map((t)=>t.data.length/t.sampleRate+Math.max(0,-t.offsetSec)),
        instAllowed?inst?.duration??0:0,
        .5,
      );
      setExportBusy(true,24);
      const rendered=await session.exporter.render({
        sampleRate:rate,
        duration:renderDuration,
        instrumental:instAllowed&&inst?{left:inst.left,right:inst.right,sampleRate:inst.sampleRate,volume:doc.mix.instrumentalVolume,pan:doc.mix.instrumentalPan,fadeInMs:doc.mix.fadeInMs,fadeOutMs:doc.mix.fadeOutMs,muted:doc.mix.instrumentalMuted}:null,
        tracks,
        masterVolume:doc.mix.masterVolume,
        limiter:settings.limiter,
        limitDb:doc.mix.masterLimitDb,
      });
      setExportBusy(true,70);
      const normalized=session.exporter.normalizeBuffer(rendered.buffer,settings);
      const encoded=await session.exporter.encode(normalized.buffer,settings);
      setExportBusy(true,94);
      const name=doc.title.replace(/[\\/:*?"<>|]+/g,'-').trim()||'vocal-studio';
      downloadBlob(encoded.blob,`${name}${onlyTrackId?`-${doc.tracks.find(t=>t.id===onlyTrackId)?.name.toLowerCase().replace(/\s+/g,'-')??'stem'}`:kind==='mix'?'-mix':'-vocal'}.${encoded.extension}`);
      setExportBusy(false,100);
      setToast({tone:encoded.fallback?'warning':'success',title:`Arquivo ${encoded.extension.toUpperCase()} pronto`,description:encoded.fallback||`${kind==='mix'?'Mix completa':'Vocal isolado'} · ${formatBytes(encoded.blob.size)} · ${normalized.appliedDb?`normalizado ${normalized.appliedDb>0?'+':''}${normalized.appliedDb.toFixed(1)} dB`:'sem normalização'}`});
    } catch(err) {
      setToast({tone:'error',title:'Exportação não concluída',description:err instanceof Error?err.message:'Tente novamente.'});
    } finally {setExportBusy(false,0);setOperationBusy(null);}
  };

  const exportStem = (trackId:string) => {
    const current=useStudioStore.getState().project;
    if(!current)return;
    const settings={...current.exportSettings,format:'wav' as const,normalize:true,includeInstrumental:false};
    void exportAudio(settings,'vocal',trackId);
  };

  const autoImprove = async () => {
    const doc=useStudioStore.getState().project;
    if(!doc)return;
    try {
      await ensureAudio();
      const tracks=doc.tracks.map((t)=>({...t,fx:{...t.fx,comp:{...t.fx.comp},reverb:{...t.fx.reverb},delay:{...t.fx.delay}}}));
      const mix={...doc.mix};
      const data=Object.fromEntries(tracks.map((t)=>[t.id,session.trackData.get(t.id)??new Float32Array(0)]));
      const inst=session.instrumental?{left:session.instrumental.left,right:session.instrumental.right}:null;
      const report=session.mixer.autoImprove({tracks,mix,instrumentalData:inst,vocalData:data,sampleRate:session.audio.sampleRate});
      updateProject({tracks:tracks.map((t)=>({...t})),mix:{...mix}});
      setToast({tone:'success',title:'Ajuste automático simples aplicado',description:report.changes.slice(0,3).join(' · ')});
    }catch(err){setToast({tone:'error',title:'Ajuste automático não concluído',description:err instanceof Error?err.message:'Tente novamente.'});}
  };

  const makePunch = () => { void startRecording(true); };

  const toggleImportModal = () => { setImportRights(false); setImportModal(true); };
  const selectInstrumental = async (file?:File) => {
    if(!file)return;
    if(!importRights){setToast({tone:'warning',title:'Confirme os direitos do áudio',description:'Marque a declaração de autorização antes de importar.'});return;}
    setImportBusy(true);
    try{await onImportInstrumental(file,true);setImportModal(false);setToast({tone:'success',title:'Instrumental autorizado importado',description:'Agora você pode pré-visualizar e exportar a mix completa.'});}
    catch(err){setToast({tone:'error',title:'Não foi possível importar',description:err instanceof Error?err.message:'Tente outro formato.'});}
    finally{setImportBusy(false);if(addInstrumentRef.current)addInstrumentRef.current.value='';}
  };

  const leave = () => {
    session.playback.stop();
    session.recorder.release();
    session.audio.setMonitor(false);
    onHome();
  };

  const waveformData = useMemo(() => {
    const result = new Map<string, Float32Array>();
    for (const track of currentProject.tracks) {
      const runtime = session.trackData.get(track.id);
      if (runtime) { result.set(track.id, runtime); continue; }
      const take = currentProject.takes.find((t) => t.id === track.activeTakeId) ??
        [...currentProject.takes].filter((t) => t.trackId === track.id).sort((a,b)=>b.index-a.index)[0];
      if (!take?.segments.length) continue;
      const sampleRate = take.segments[0].sampleRate;
      const merged = track.comping && track.regionMap?.length
        ? session.takes.buildTrackAudio(track, currentProject.takes, { sampleRate, start: 0, duration: duration || undefined })
        : mergeSegments(take.segments, { sampleRate, start: 0, duration: duration || undefined });
      if (merged.data.length) result.set(track.id, merged.data);
    }
    return result;
  }, [currentProject.tracks, currentProject.takes, duration, micReady]);

  const currentTab = (id: PanelTab) => setPanel(id);
  const menuTabs = TAB_META.filter((t)=>isSimpleMode?['record','timeline','mix','fx','sync','export'].includes(t.id):true);

  return (
    <main className="studio-page">
      <header className="studio-topbar">
        <div className="studio-topbar-left"><button className="brand brand-button" onClick={leave} title="Voltar ao início"><span className="brand-mark"><Icon name="logo" size={18}/></span><span className="brand-name">VOCAL<span>STUDIO</span></span></button><span className="topbar-divider"/><ProjectTitleEditor/><span className={`source-mode-pill source-${currentProject.sourceType}`}>{currentProject.sourceType==='youtube'?<Icon name="youtube" size={13}/>:currentProject.sourceType==='file'?<Icon name="wave" size={13}/>:<Icon name="mic" size={13}/>} {currentProject.sourceType==='youtube'?'YOUTUBE':currentProject.sourceType==='file'?'ESTÚDIO':'GRAVAÇÃO RÁPIDA'}</span></div>
        <div className="studio-topbar-right">
          <span className="save-status">{saving?<><span className="save-spinner"/>Salvando…</>:dirty?<><span className="save-dot save-pending"/>Alterações pendentes</>:<><span className="save-dot"/>Salvo neste dispositivo</>}</span>
          <button className={`mode-switch ${isSimpleMode?'mode-easy':'mode-pro'}`} onClick={()=>{const next=!isSimpleMode;setSimpleMode(next);updateProject({ui:{...currentProject.ui,mode:next?'easy':'pro'}});}}><span>{isSimpleMode?'MODO FÁCIL':'AVANÇADO'}</span><Icon name="chevronDown" size={13}/></button>
          <button className="icon-btn topbar-projects" onClick={onOpenProjects} title="Meus projetos"><Icon name="folder" size={17}/></button>
          {youtubeMode&&!hasInstrument&&<button className="btn btn-secondary add-instrument-top" onClick={toggleImportModal}><Icon name="upload" size={15}/>Instrumental</button>}
        </div>
      </header>

      <div className="studio-main">
        <div className="studio-session-header"><div className="session-title-block"><span className="section-kicker">SEU ESTÚDIO <i className="session-status-led"/></span><h1>{currentProject.youtubeTitle||currentProject.instrumental?.name||currentProject.title}</h1><div className="session-meta"><span>{youtubeMode?'PLAYER OFICIAL DO YOUTUBE':hasInstrument?'INSTRUMENTAL AUTORIZADO':'FAIXA VOCAL'}</span><i/> <span>{duration?formatTime(duration):'Duração não informada'}</span>{hasVocal&&<><i/><span>{currentProject.takes.length} take{currentProject.takes.length===1?'':'s'}</span></>}</div></div><div className="session-privacy"><Icon name="shield" size={16}/><span>Áudio processado<br/><b>neste dispositivo</b></span></div></div>

        <section className="reference-card">
          <div className="reference-card-head"><span className="reference-source-icon">{youtubeMode?<Icon name="youtube" size={15}/>:hasInstrument?<Icon name="wave" size={15}/>:<Icon name="mic" size={15}/>}</span><div><b>{youtubeMode?'MÚSICA DE REFERÊNCIA':hasInstrument?'INSTRUMENTAL':'GRAVAÇÃO RÁPIDA'}</b><span>{youtubeMode?'Player oficial · o áudio não é extraído':hasInstrument?currentProject.instrumental?.name??'Instrumental local':'Grave uma ideia sem base'}</span></div><div className="reference-head-right">{youtubeMode&&<span className={`player-live-status ${playerReady?'player-live-ready':''}`}><i/>{playerReady?'PLAYER PRONTO':playerState==='buffering'?'CARREGANDO':'CONECTANDO'}</span>}{hasInstrument&&<span className="local-audio-tag"><Icon name="shield" size={12}/> LOCAL</span>}</div></div>
          {youtubeMode&&currentProject.youtubeVideoId?<YouTubePlayer videoId={currentProject.youtubeVideoId}/>:hasInstrument?<div className="local-track-visual"><div className="local-track-art"><Icon name="wave" size={23}/><span>LOCAL<br/>AUDIO</span></div><div className="local-track-info"><b>{currentProject.instrumental?.name||'Instrumental autorizado'}</b><span>{formatTime(currentProject.instrumentalBuffer?.duration??duration)} · {currentProject.instrumentalBuffer?.sampleRate?`${Math.round(currentProject.instrumentalBuffer.sampleRate/1000)} kHz`:''} · processado no navegador</span><div className="local-wave-mini">{Array.from({length:80},(_,i)=><i key={i} style={{height:`${6+Math.abs(Math.sin(i*.38)*Math.cos(i*.11))*27}px`}}/>)}</div></div></div>:<div className="quick-reference-empty"><span className="quick-ref-circle"><Icon name="mic" size={21}/></span><div><b>Sem instrumental por enquanto</b><span>Grave uma ideia. Você pode importar uma base autorizada depois.</span></div><button className="btn btn-outline btn-xs" onClick={toggleImportModal}><Icon name="upload" size={13}/>Adicionar instrumental</button></div>}
          {sourceError&&<div className="source-error"><Icon name="alert" size={15}/><span>{sourceError}</span><a href={`https://www.youtube.com/watch?v=${currentProject.youtubeVideoId}`} target="_blank" rel="noreferrer">Abrir no YouTube <Icon name="external" size={13}/></a></div>}
          <div className="transport-row"><div className="transport-times"><span className="time-current">{formatTime(currentPosition)}</span><span className="time-slash">/</span><span className="time-total">{formatTime(duration)}</span></div><input className="transport-seek" type="range" min="0" max={Math.max(0.01,duration||1)} step="0.05" value={Math.min(currentPosition,duration||0)} onChange={(e)=>changePlaybackTime(Number(e.target.value))} aria-label="Posição da música"/>{selectedRegion&&<button className="clear-region" title="Limpar seleção" onClick={()=>setSelectedRegion(null)}><Icon name="close" size={13}/></button>}</div>
          <div className="transport-controls"><div className="transport-left"><button className="transport-small" onClick={()=>changePlaybackTime(Math.max(0,currentPosition-10))} title="Voltar 10 segundos"><Icon name="skipBack" size={15}/><small>10</small></button><button className="transport-main" onClick={togglePlayback} disabled={youtubeMode&&!playerReady&&!sourceError} title={session.playback.status==='playing'?'Pausar':'Reproduzir'}><Icon name={session.playback.status==='playing'?'pause':'play'} size={18}/></button><button className="transport-small" onClick={()=>changePlaybackTime(Math.min(duration,currentPosition+10))} title="Avançar 10 segundos"><Icon name="skipForward" size={15}/><small>10</small></button><button className="transport-stop" onClick={stopPlayback} title="Parar"><Icon name="stop" size={15}/></button></div><div className="transport-right"><label className="metronome-mini"><input type="checkbox" checked={currentProject.ui.metronome} onChange={(e)=>updateProject({ui:{...currentProject.ui,metronome:e.target.checked}})}/><Icon name="metronome" size={14}/><span>METRÔNOMO</span></label><select className="speed-select" value={currentProject.ui.speed} onChange={(e)=>{const r=Number(e.target.value);updateProject({ui:{...currentProject.ui,speed:r}});session.adapter?.setPlaybackRate(r);}} title="Velocidade de reprodução"><option value="0.75">0,75×</option><option value="0.9">0,90×</option><option value="1">1,00×</option><option value="1.1">1,10×</option><option value="1.25">1,25×</option></select></div></div>
        </section>

        <section className="timeline-card">
          <div className="timeline-card-top"><div><span className="section-kicker">TIMELINE MULTIFAIXA</span><span className="timeline-track-count">{(hasInstrument?1:0)+currentProject.tracks.length} FAIXA{(hasInstrument?1:0)+currentProject.tracks.length===1?'':'S'}</span></div><div className="timeline-tools"><span className="selection-hint"><Icon name="scissors" size={13}/>Arraste para selecionar um trecho</span>{currentProject.ui.metronome&&<span className="metronome-on"><Icon name="metronome" size={13}/> {currentProject.ui.bpm} BPM</span>}</div></div>
          {hasInstrument&&<div className="timeline-lane"><div className="lane-label"><span className="lane-color" style={{background:'#f5c451'}}/><span>INSTRUMENTAL</span><span className="lane-time">{formatTime(currentProject.instrumentalBuffer?.duration??0)}</span></div><Waveform data={session.instrumental?.left} duration={duration} currentTime={currentPosition} color="#e6b845" height={46} compact interactive selectedRegion={selectedRegion} markers={currentProject.markers.map((m)=>({id:m.id,time:m.time,label:m.label,color:'#eabf50'}))} onSeek={changePlaybackTime} onSelectRegion={setSelectedRegion}/></div>}
          {currentProject.tracks.map((track)=>{
            const data=waveformData.get(track.id)??null;
            const trackDuration=data?data.length/session.audio.sampleRate:0;
            return <div className="timeline-lane" key={track.id}>
              <div className="lane-label"><span className="lane-color" style={{background:track.color}}/><span>{track.name}</span><span className="lane-time">{data?formatTime(trackDuration):'—'}</span></div>
              <Waveform data={data} duration={duration||trackDuration} currentTime={currentPosition} color={track.color} height={54} compact interactive selectedRegion={selectedRegion} markers={currentProject.markers.map((m)=>({id:m.id,time:m.time,label:m.label,color:'#eabf50'}))} onSeek={changePlaybackTime} onSelectRegion={setSelectedRegion}/>
            </div>})}
          {!hasVocal&&<div className="timeline-empty-note"><Icon name="mic" size={14}/>Sua forma de onda vocal aparecerá aqui após o primeiro take.</div>}
          {selectedRegion&&<div className="selection-action-row"><span><Icon name="scissors" size={14}/>Trecho selecionado: <b>{formatTime(selectedRegion.start)} → {formatTime(selectedRegion.end)}</b> · {formatTime(selectedRegion.end-selectedRegion.start)}</span><button className="text-button" onClick={()=>setSelectedRegion(null)}>Limpar seleção</button><button className="btn btn-outline btn-xs" onClick={makePunch} disabled={!micReady||recording}><Icon name="record" size={13}/>Regravar trecho</button></div>}
        </section>

        <section className={`record-transport ${recording?'record-transport-active':''}`}>
          <div className="rec-status"><span className={`rec-led ${recording&&!recordingPaused?'rec-led-on':''}`}/><div><b>{countdownLabel?`COMEÇA EM ${countdownLabel}`:recordingPaused?'GRAVAÇÃO PAUSADA':recording?'GRAVANDO AGORA':hasVocal?'PRONTO PARA OUTRO TAKE':'PRONTO PARA GRAVAR'}</b><span>{recording?`Take em andamento · ${recordingPaused?'pausado':`nível de entrada ${micReady?'ativo':'—'}`}`:micReady?'Coloque os fones e cante no seu nível normal.':'Ative o microfone no painel abaixo.'}</span></div></div>
          <div className="record-actions">{recording?<><div className="record-timer">{formatTime(recordingSeconds,true)}</div><button className="btn btn-secondary record-pause-btn" onClick={toggleRecordPause}><Icon name={recordingPaused?'play':'pause'} size={15}/>{recordingPaused?'Continuar':'Pausar'}</button><button className="btn btn-stop-record" onClick={()=>void stopRecording(false)} disabled={operationBusy==='stopping'}><Icon name="stop" size={14}/>Finalizar take</button></>:<button className="btn btn-record" onClick={()=>void startRecording(false)} disabled={!micReady||!!operationBusy||youtubeMode&&!playerReady}><Icon name="record" size={18}/><span>{operationBusy==='record'?'Preparando…':'GRAVAR TAKE'}</span><small>● REC</small></button>}</div>
        </section>

        <nav className="studio-tabs" aria-label="Painéis do estúdio">{menuTabs.map((t)=><button className={`studio-tab ${panel===t.id?'studio-tab-active':''} ${t.id==='export'?'studio-tab-export':''}`} key={t.id} onClick={()=>currentTab(t.id)}><Icon name={t.icon} size={15}/><span className="tab-full-label">{t.label}</span><span className="tab-mobile-label">{t.mobile}</span>{t.id==='takes'&&hasVocal&&<i className="tab-count">{currentProject.takes.length}</i>}</button>)}</nav>

        <section className="studio-panel">
          {panel==='record'&&<RecordPanel onEnableMic={enableMic} onSwitchMic={switchMic} onPunch={makePunch} onInputGainBoostChange={(percent)=>session.recorder.setRecordingGainBoostPercent(percent)} busy={!!operationBusy}/>}
          {panel==='timeline'&&<TimelinePanel onSeek={changePlaybackTime}/>}
          {panel==='mix'&&<MixPanel onAutoImprove={autoImprove}/>}
          {panel==='fx'&&<FxPanel onNoiseReduce={runNoiseReduction} onBeforeAfter={toggleBeforeAfter} isDenoising={denoising}/>}
          {panel==='sync'&&<SyncPanel onCalibrate={calibrate} calibrationBusy={calibrationBusy}/>}
          {panel==='takes'&&<TakesPanel onPlayTake={playTake} onDeleteTake={deleteTake} onCompare={toggleCompare} onMakeComp={makeComping} compareId={compareTakeId}/>}
          {panel==='analysis'&&<AnalysisPanel onAnalyze={analyzeTake} busy={analysisBusy}/>}
          {panel==='export'&&<ExportPanel onExport={exportAudio} onExportStem={exportStem} busy={exportBusy} capabilities={exportCapabilities}/>}
        </section>

        <footer className="studio-footer"><span><Icon name="shield" size={13}/>Gravações processadas localmente sempre que possível.</span><span>VOCAL STUDIO · BETA</span><button onClick={leave}>Sair do projeto <Icon name="chevronRight" size={13}/></button></footer>
      </div>

      {importModal&&<div className="modal-backdrop" onClick={()=>!importBusy&&setImportModal(false)}><div className="modal-card import-modal" onClick={(e)=>e.stopPropagation()}><button className="modal-close icon-btn" onClick={()=>setImportModal(false)} disabled={importBusy}><Icon name="close" size={17}/></button><span className="modal-icon"><Icon name="upload" size={21}/></span><span className="section-kicker">INSTRUMENTAL AUTORIZADO</span><h2>Importe sua base</h2><p>Este áudio será armazenado localmente neste dispositivo e usado para montar uma mix exportável.</p><label className="rights-check rights-modal-check"><input type="checkbox" checked={importRights} onChange={(e)=>setImportRights(e.target.checked)}/><span className="custom-check"><Icon name="check" size={13}/></span><span>Declaro que possuo autorização ou direitos necessários para usar este áudio.</span></label><input ref={addInstrumentRef} type="file" className="file-input-hidden" accept="audio/*,.mp3,.wav,.m4a,.ogg,.aac" onChange={(e)=>void selectInstrumental(e.target.files?.[0])}/><button className="btn btn-primary full-button" disabled={!importRights||importBusy} onClick={()=>addInstrumentRef.current?.click()}>{importBusy?<><span className="button-spinner"/>Importando…</>:<><Icon name="upload" size={16}/>Escolher arquivo de áudio</>}</button><small className="modal-supported">MP3 · WAV · M4A · OGG · AAC (quando suportado)</small></div></div>}
    </main>
  );
}

function downloadBlob(blob:Blob, filename:string){const url=URL.createObjectURL(blob);const a=document.createElement('a');a.href=url;a.download=filename;document.body.appendChild(a);a.click();a.remove();setTimeout(()=>URL.revokeObjectURL(url),1500);}
